import { call, put, select } from 'redux-saga/effects';

import { resetPartnerPatient, updatePatient } from 'connect/actions';
import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { selectDataPartnerPatient, selectDataPatient, selectDataUser } from 'connect/selectors';
import {
  apiGetEmailVerificationCode as apiRequestEmailVerification,
  apiGetPhoneVerificationCode as apiRequestPhoneVerification,
  apiGetPhoneVerificationSmsStatus as apiRequestPhoneVerificationSmsStatus,
  apiVerifyEmail,
  apiVerifyPhone,
} from 'endpoints/patient-endpoints';

import { SagaHandler } from '../SagaHandler';

class CommonSagaHandler extends SagaHandler {
  constructor({ actionName, stateKey }) {
    super({ actionName, stateKey });
  }

  /**
   * Retrieves the patient information for checkout based on the current user's context.
   */
  * _getCheckoutPatient() {
    const currentUser = yield select(selectDataUser);
    const isPartnerUser = !!currentUser?.partner;

    const patient = isPartnerUser ? yield select(selectDataPartnerPatient) : yield select(selectDataPatient);

    return { patient, isPartner: isPartnerUser };
  }
}

class _RequestEmailVerification extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/REQUEST_EMAIL_VERIFICATION',
      stateKey: 'requestPatientEmailVerification',
    });
  }

  createAction() {
    return {
      type: this._actionName,
    };
  }

  * saga() {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const patient = yield select(selectDataPatient);
    const isLoading = yield select(loadingHandler.selector);
    if (isLoading || !patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      yield call(apiRequestEmailVerification, patient.id);
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'RequestEmailVerification');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

class _VerifyEmail extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/VERIFY_EMAIL',
      stateKey: 'verifyEmail',
    });
  }

  createAction(code) {
    return {
      type: this._actionName,
      code,
    };
  }

  * saga({ code }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const patient = yield select(selectDataPatient);
    const isLoading = yield select(loadingHandler.selector);
    if (isLoading || !patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      const updatedPatient = yield call(apiVerifyEmail, patient.id, code);
      yield put(updatePatient(updatedPatient));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'VerifyEmail');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

class _RequestPhoneVerification extends CommonSagaHandler {
  constructor() {
    super({
      actionName: 'data/REQUEST_PHONE_VERIFICATION',
      stateKey: 'requestMobilePhoneVerification',
    });
  }

  createAction(method = 'text') {
    return {
      type: this._actionName,
      method,
    };
  }

  * saga({ method }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const isLoading = yield select(loadingHandler.selector);

    if (isLoading) {
      return;
    }

    const { patient } = yield this._getCheckoutPatient();

    if (!patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      yield call(apiRequestPhoneVerification, patient.id, method);
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'RequestPhoneVerification');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

class _VerifyPhone extends CommonSagaHandler {
  constructor() {
    super({
      actionName: 'data/VERIFY_PHONE',
      stateKey: 'verifyPhone',
    });
  }

  createAction(code) {
    return {
      type: this._actionName,
      code,
    };
  }

  * saga({ code }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const isLoading = yield select(loadingHandler.selector);

    if (isLoading) {
      return;
    }

    const { patient, isPartner } = yield this._getCheckoutPatient();

    if (!patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      const updatedPatient = yield call(apiVerifyPhone, patient.id, code);

      if (isPartner) {
        yield put(resetPartnerPatient(updatedPatient));
      } else {
        yield put(updatePatient(updatedPatient));
      }

      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'VerifyPhone');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

class _RequestPhoneVerificationSmsStatus extends CommonSagaHandler {
  constructor() {
    super({
      actionName: 'data/REQUEST_PHONE_VERIFICATION_SMS_STATUS',
      stateKey: 'phoneVerificationSmsStatus',
    });
  }

  createAction() {
    return {
      type: this._actionName,
    };
  }

  * saga() {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;
    const valueHandler = this.value;

    const isLoading = yield select(loadingHandler.selector);

    if (isLoading) {
      return;
    }

    const { patient } = yield this._getCheckoutPatient();

    if (!patient?.id) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(valueHandler.update(null));

    try {
      const smsStatus = yield call(apiRequestPhoneVerificationSmsStatus, patient.id);
      yield put(valueHandler.update(smsStatus));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'RequestPhoneVerificationSmsStatus');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const RequestEmailVerification = new _RequestEmailVerification();
export const VerifyEmail = new _VerifyEmail();
export const RequestPhoneVerification = new _RequestPhoneVerification();
export const VerifyPhone = new _VerifyPhone();
export const RequestPhoneVerificationSmsStatus = new _RequestPhoneVerificationSmsStatus();
